import { Grid, Card, Text, Title, Group, Stack, Badge, Button, Loader, Alert } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import {
  IconTruck,
  IconFile,
  IconCar,
  IconUsers,
  IconBuildingCommunity,
  IconMapPin,
  IconUserCog,
  IconAlertCircle,
  IconPlus,
  IconArrowRight,
  IconCalendar,
} from '@tabler/icons-react';
import { LineChart } from '@mantine/charts';
import { useMemo } from 'react';
import {
  useDeCAs,
  useEnvios,
  useVehiculos,
  useConductores,
  useEmpresas,
  useOrigenesDestinos,
  useUsuarios,
} from '../api/hooks';
import type { DeCaEnriched } from '../api/types';

/**
 * Dashboard con datos en vivo.
 *
 * Contadores calculados desde los hooks actuales (no datos hardcoded).
 * - DeCA hoy: cuenta DeCAs cuya fechaEmision cae en el día local.
 * - En curso: envios con estado EN_CURSO + DeCAs VIGENTES.
 * - Vehículos activos, Conductores activos, Empresas activas, etc.
 * - Gráfico: DeCAs emitidos por día en los últimos 7 días.
 * - Lista de últimos DeCAs (4 más recientes).
 */
export function Dashboard() {
  const navigate = useNavigate();

  const decas = useDeCAs();
  const envios = useEnvios();
  const vehiculos = useVehiculos();
  const conductores = useConductores();
  const empresas = useEmpresas();
  const origenes = useOrigenesDestinos();
  const usuarios = useUsuarios();

  const decasData = useMemo(() => decas.data ?? [], [decas.data]);
  const enviosData = useMemo(() => envios.data ?? [], [envios.data]);
  const vehiculosData = useMemo(() => vehiculos.data ?? [], [vehiculos.data]);
  const conductoresData = useMemo(() => conductores.data ?? [], [conductores.data]);
  const empresasData = useMemo(() => empresas.data ?? [], [empresas.data]);
  const origenesData = useMemo(() => origenes.data ?? [], [origenes.data]);
  const usuariosData = useMemo(() => usuarios.data ?? [], [usuarios.data]);

  const isLoadingAny =
    decas.isLoading ||
    envios.isLoading ||
    vehiculos.isLoading ||
    conductores.isLoading ||
    empresas.isLoading ||
    origenes.isLoading ||
    usuarios.isLoading;

  const hasError =
    decas.error ||
    envios.error ||
    vehiculos.error ||
    conductores.error ||
    empresas.error ||
    origenes.error ||
    usuarios.error;

  // Contadores en vivo
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const decaHoyCount = decasData.filter((d) => {
    const e = new Date(d.fechaEmision);
    return e >= today;
  }).length;

  const enCursoCount = enviosData.filter((e) => e.estado === 'EN_CURSO').length;
  const enviosBorradorCount = enviosData.filter((e) => e.estado === 'BORRADOR').length;
  const decasVigentesCount = decasData.filter((d) => d.estado === 'VIGENTE').length;
  const vehiculosActivosCount = vehiculosData.filter((v) => v.activo !== false).length;
  const conductoresActivosCount = conductoresData.filter((c) => c.activo !== false).length;
  const usuariosActivosCount = usuariosData.filter((u) => u.active).length;
  const empresasActivasCount = empresasData.filter((e) => e.activo !== false).length;
  const origenesActivosCount = origenesData.filter((o) => o.activo !== false).length;

  // Servicios / envios cuya fecha de servicio cae en los próximos 7 días.
  // Proxy operativo: cada envio implica al menos un servicio. Asi evitamos
  // hidratar useServicios por cada DeCA (que sería N+1).
  const today7 = new Date(today);
  today7.setDate(today.getDate() + 7);
  const serviciosProximosCount = enviosData.filter((e) => {
    if (!e.fechaServicio) return false;
    const fs = new Date(e.fechaServicio);
    return fs >= today && fs <= today7;
  }).length;

  // Series para el gráfico: ultimos 7 días
  const chartData = useMemo(() => {
    const days: { date: string; iso: string; deca: number }[] = [];
    const fmt = new Intl.DateTimeFormat('es-ES', { weekday: 'short' });
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const next = new Date(d);
      next.setDate(d.getDate() + 1);
      const count = decasData.filter((dc) => {
        const fe = new Date(dc.fechaEmision);
        return fe >= d && fe < next;
      }).length;
      days.push({
        date: fmt.format(d).replace('.', ''),
        iso: d.toISOString().slice(0, 10),
        deca: count,
      });
    }
    return days;
  }, [decasData]);

  // 4 DeCAs mas recientes
  const ultimosDecas: DeCaEnriched[] = useMemo(() => {
    return [...decasData].sort((a, b) => (a.fechaEmision < b.fechaEmision ? 1 : -1)).slice(0, 4);
  }, [decasData]);

  const kpis = [
    {
      label: 'DeCA hoy',
      value: decaHoyCount,
      color: 'deca.7',
      icon: IconFile,
      hint: `${decasVigentesCount} vigentes en total`,
    },
    {
      label: 'En curso',
      value: enCursoCount,
      color: 'blue',
      icon: IconTruck,
      hint: `${enviosBorradorCount} borradores sin emitir`,
    },
    {
      label: 'Vehículos activos',
      value: vehiculosActivosCount,
      color: 'green',
      icon: IconCar,
      hint: `de ${vehiculosData.length} totales`,
    },
    {
      label: 'Conductores activos',
      value: conductoresActivosCount,
      color: 'violet',
      icon: IconUsers,
      hint: `de ${conductoresData.length} totales`,
    },
    {
      label: 'Empresas activas',
      value: empresasActivasCount,
      color: 'deca.7',
      icon: IconBuildingCommunity,
      hint: `${empresasData.filter((e) => e.tipo === 'AMBOS').length} mixtas`,
    },
    {
      label: 'Orígenes / Destinos',
      value: origenesActivosCount,
      color: 'orange',
      icon: IconMapPin,
      hint: `en el catálogo`,
    },
    {
      label: 'Usuarios activos',
      value: usuariosActivosCount,
      color: 'indigo',
      icon: IconUserCog,
      hint: `de ${usuariosData.length} totales`,
    },
    {
      label: 'Servicios próximos (7 días)',
      value: serviciosProximosCount,
      color: 'cyan',
      icon: IconCalendar,
      hint: 'envíos con fecha de servicio en próximos 7 días',
    },
  ];

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <div>
          <Title order={2}>Dashboard</Title>
          <Text size="sm" c="dimmed">
            Estado en vivo. Los contadores se recalculan al cambiar el catálogo de tableros.
          </Text>
        </div>
        <Group gap="sm">
          <Button
            variant="default"
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/envios/nuevo')}
          >
            Nuevo envío
          </Button>
          <Button leftSection={<IconFile size={16} />} onClick={() => navigate('/deca')}>
            Ver DeCAs
          </Button>
        </Group>
      </Group>

      {hasError && (
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="No se pudieron cargar algunos datos"
        >
          Verifica tu conexión. Los contadores podrían estar parciales.
        </Alert>
      )}

      {isLoadingAny && (
        <Group gap="xs" c="dimmed">
          <Loader size="xs" />
          <Text size="sm">Cargando datos en vivo…</Text>
        </Group>
      )}

      <Grid>
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Grid.Col key={k.label} span={{ base: 12, xs: 6, md: 3 }}>
              <Card shadow="sm" padding="md" radius="md" withBorder h="100%">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      {k.label}
                    </Text>
                    <Text fz={32} fw={700} c={k.color} lh={1}>
                      {k.value}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {k.hint}
                    </Text>
                  </Stack>
                  <Icon size={32} color="gray" stroke={1.4} />
                </Group>
              </Card>
            </Grid.Col>
          );
        })}
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Card shadow="sm" padding="md" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Title order={4}>DeCA emitidos (últimos 7 días)</Title>
              <Badge variant="light" color="deca">
                {chartData.reduce((acc, d) => acc + d.deca, 0)} en total
              </Badge>
            </Group>
            {chartData.some((d) => d.deca > 0) ? (
              <LineChart
                h={260}
                data={chartData}
                dataKey="date"
                series={[{ name: 'deca', color: 'deca.7', label: 'DeCA' }]}
                curveType="linear"
                withDots
                withLegend={false}
                yAxisProps={{ allowDecimals: false }}
              />
            ) : (
              <Alert color="gray" variant="light">
                No hay DeCAs emitidos en los últimos 7 días.
              </Alert>
            )}
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card shadow="sm" padding="md" radius="md" withBorder h="100%">
            <Group justify="space-between" mb="md">
              <Title order={4}>Últimos DeCA</Title>
              <Button
                variant="subtle"
                rightSection={<IconArrowRight size={14} />}
                size="xs"
                onClick={() => navigate('/deca')}
              >
                Ver todos
              </Button>
            </Group>
            {ultimosDecas.length === 0 ? (
              <Stack gap="xs" align="center" py="xl">
                <Text c="dimmed" size="sm">
                  Aún no hay DeCAs emitidos.
                </Text>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => navigate('/envios')}
                >
                  Crear desde un envío
                </Button>
              </Stack>
            ) : (
              <Stack gap="xs">
                {ultimosDecas.map((d) => (
                  <Group
                    key={d.id}
                    justify="space-between"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/deca/${d.id}`)}
                  >
                    <Stack gap={0}>
                      <Text size="sm" ff="monospace">
                        {d.codigoAnual ?? d.codigoPublico}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {new Date(d.fechaEmision).toLocaleString()}
                      </Text>
                    </Stack>
                    <Badge size="sm" variant="light" color={decaEstadoColor(d.estado)}>
                      {d.estado}
                    </Badge>
                  </Group>
                ))}
              </Stack>
            )}
          </Card>
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card shadow="sm" padding="md" radius="md" withBorder>
            <Title order={4} mb="sm">
              Acciones rápidas
            </Title>
            <Stack gap="xs">
              <Button
                variant="light"
                justify="flex-start"
                leftSection={<IconTruck size={16} />}
                rightSection={<IconArrowRight size={14} />}
                onClick={() => navigate('/envios/nuevo')}
              >
                Nuevo envío
              </Button>
              <Button
                variant="light"
                justify="flex-start"
                leftSection={<IconFile size={16} />}
                rightSection={<IconArrowRight size={14} />}
                onClick={() => navigate('/deca')}
              >
                Ver DeCAs emitidos
              </Button>
              <Button
                variant="light"
                justify="flex-start"
                leftSection={<IconMapPin size={16} />}
                rightSection={<IconArrowRight size={14} />}
                onClick={() => navigate('/origenes-destinos')}
              >
                Catálogo de orígenes / destinos
              </Button>
              <Button
                variant="light"
                justify="flex-start"
                leftSection={<IconUserCog size={16} />}
                rightSection={<IconArrowRight size={14} />}
                onClick={() => navigate('/usuarios')}
              >
                Gestión de usuarios
              </Button>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card shadow="sm" padding="md" radius="md" withBorder>
            <Title order={4} mb="sm">
              Envíos pendientes
            </Title>
            {enviosBorradorCount === 0 ? (
              <Text c="dimmed" size="sm">
                No hay envíos en borrador pendientes de emitir.
              </Text>
            ) : (
              <Stack gap="xs">
                {enviosData
                  .filter((e) => e.estado === 'BORRADOR')
                  .slice(0, 4)
                  .map((e) => (
                    <Group
                      key={e.id}
                      justify="space-between"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/envios/${e.id}`)}
                    >
                      <Stack gap={0}>
                        <Text size="sm" ff="monospace">
                          {e.id.slice(0, 8)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {e.fechaServicio} · {e.tipoServicio.replace(/_/g, ' ')}
                        </Text>
                      </Stack>
                      <Badge size="sm" variant="light" color="gray">
                        Borrador
                      </Badge>
                    </Group>
                  ))}
                <Button
                  variant="subtle"
                  size="xs"
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => navigate('/envios')}
                >
                  Ver todos los envíos
                </Button>
              </Stack>
            )}
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function decaEstadoColor(estado: string): string {
  switch (estado) {
    case 'VIGENTE':
      return 'green';
    case 'EN_CURSO':
      return 'blue';
    case 'FINALIZADO':
      return 'dark';
    case 'CANCELADO':
      return 'red';
    default:
      return 'gray';
  }
}
